import ApiService from "../../services/ApiService";
import UtilService from "../../services/UtilService";

/**
 * Helpers for HomeStoryRo rows from GET /home/story (list/detail).
 */

function nonEmptyTrimmed(v) {
    if (v == null) return null;
    const s = String(v).trim();
    return s !== "" ? s : null;
}

/** Indexable article URL for a published home story. */
export function buildReadStoryPath(storyId) {
    const id = nonEmptyTrimmed(storyId);
    return id ? `/read-story/${encodeURIComponent(id)}` : "";
}

/** Crossword **instance** id from story rows or GET `/home/puzzle/:templateId`. */
export function extractCrosswordPlayInstanceIdFromPayload(data) {
    if (!data || typeof data !== "object") return null;
    return (
        nonEmptyTrimmed(data.crossword?.id)
        ?? nonEmptyTrimmed(data.crossWord?.id)
        ?? nonEmptyTrimmed(data.crossTpl?.crossword?.id)
        ?? nonEmptyTrimmed(data.crossTpl?.crossword_id)
        ?? nonEmptyTrimmed(data.crossTpl?.crosswordId)
        ?? nonEmptyTrimmed(data.cross_tpl?.crossword?.id)
    );
}

/** Grid puzzle **instance** id from template/home payloads. */
export function extractGridPlayInstanceIdFromPayload(data) {
    if (!data || typeof data !== "object") return null;
    return (
        nonEmptyTrimmed(data.grid?.id)
        ?? nonEmptyTrimmed(data.grid?.grid?.id)
        ?? nonEmptyTrimmed(data.gridTpl?.grid?.id)
        ?? nonEmptyTrimmed(data.gridTpl?.grid_id)
        ?? nonEmptyTrimmed(data.grid_tpl?.grid?.id)
    );
}

/**
 * Grid play id for `/story/play-grid/*` and `/home/grid/:id` — instance id (`grid.id`), not grid-tpl id.
 */
export function getHomeStoryGridPlayInstanceId(ro) {
    return (
        extractGridPlayInstanceIdFromPayload(ro)
        ?? nonEmptyTrimmed(ro?.gridId)
    );
}

export function getHomeStoryCrossPlayInstanceId(ro) {
    return extractCrosswordPlayInstanceIdFromPayload(ro);
}

/** False if tag label should be hidden in UI (e.g. infra / Bedrock). */
function isBedrockTagLabel(label) {
    return String(label || "").toLowerCase().includes("bedrock");
}

/** Tags as string[], from array of strings or { tag?, name? }. Omits Bedrock-related tags for display. */
export function normalizeStoryRoTags(tags) {
    if (!Array.isArray(tags)) return [];
    return tags
        .map((t) =>
            typeof t === "string" ? t.trim() : String(t?.tag ?? t?.name ?? "").trim()
        )
        .filter(Boolean)
        .filter((label) => !isBedrockTagLabel(label));
}

/**
 * Story blob on HomeStoryRo: top-level `story`, or nested `crossword.crossTpl.story`
 * (and similar grid paths), e.g. GET `/home/story/story-of-the-day`.
 */
export function resolveHomeStoryBlob(ro) {
    if (!ro || typeof ro !== "object") return null;
    if (ro.story && typeof ro.story === "object") return ro.story;
    const crossTplStory =
        ro.crossword?.crossTpl?.story ??
        ro.crossword?.cross_tpl?.story ??
        ro.crossTpl?.story ??
        ro.cross_tpl?.story;
    if (crossTplStory && typeof crossTplStory === "object") return crossTplStory;
    const crossStory = ro.crossword?.story;
    if (crossStory && typeof crossStory === "object") return crossStory;
    const gridTplStory =
        ro.grid?.gridTpl?.story ??
        ro.grid?.grid_tpl?.story ??
        ro.gridTpl?.story ??
        ro.grid_tpl?.story;
    if (gridTplStory && typeof gridTplStory === "object") return gridTplStory;
    const gridStory = ro.grid?.story;
    if (gridStory && typeof gridStory === "object") return gridStory;
    const templateStory = ro.template?.story;
    if (templateStory && typeof templateStory === "object") return templateStory;
    return null;
}

/** Story UUID for `/share/{id}/story` from home story row (e.g. `crossword.story.id`). */
export function resolveHomeStoryShareId(ro) {
    const blob = resolveHomeStoryBlob(ro);
    return nonEmptyTrimmed(blob?.id) ?? "";
}

function homeStoryPuzzleType(ro) {
    if (!ro || typeof ro !== "object") return "";
    let raw = String(ro?.puzzleType ?? ro?.puzzle_type ?? ro?.kind ?? "").toUpperCase();
    if (!raw) {
        if (ro.crossword && typeof ro.crossword === "object") raw = "CROSSWORD";
        else if (
            ro.crossTpl &&
            typeof ro.crossTpl === "object" &&
            String(ro.crossTpl.type ?? "").toUpperCase() === "CROSSWORD"
        ) {
            raw = "CROSSWORD";
        } else if (ro.grid != null || ro.gridTpl != null) raw = "GRID";
    }
    /** Carousel / home rows often use `kind: GRID` with WORD_SEARCH | HIDDEN_MESSAGE on `grid.gridTpl.type`. */
    if (raw === "GRID") {
        return String(
            ro?.grid?.gridTpl?.type ?? ro?.gridTpl?.type ?? ro?.template?.type ?? ""
        ).toUpperCase();
    }
    if (!raw && ro.template && typeof ro.template === "object") {
        raw = String(ro.template.type ?? "").toUpperCase();
    }
    return raw;
}

/** Puzzle kind for UI labels: `CROSSWORD` | `WORD_SEARCH` | `HIDDEN_MESSAGE` | `""`. */
export function getHomeStoryPuzzleType(ro) {
    return homeStoryPuzzleType(ro);
}

/** Template id for {@link fetchStoryPlayPathByTemplate} when instance ids are absent on the row. */
export function getHomeStoryTemplateIdForPlayFetch(ro, type) {
    if (!ro || typeof ro !== "object") return null;
    const generic =
        nonEmptyTrimmed(ro.templateId)
        ?? nonEmptyTrimmed(ro.template?.id)
        ?? null;
    if (type === "CROSSWORD") {
        return (
            generic
            ?? nonEmptyTrimmed(ro.crossTplId)
            ?? nonEmptyTrimmed(ro.crossTpl?.id)
            ?? nonEmptyTrimmed(ro.crossword?.crossTpl?.id)
        );
    }
    if (type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") {
        return (
            generic
            ?? nonEmptyTrimmed(ro.gridTplId)
            ?? nonEmptyTrimmed(ro.gridTpl?.id)
            ?? nonEmptyTrimmed(ro.grid?.gridTpl?.id)
        );
    }
    return generic;
}

/**
 * Nested cross/grid template for headlines — includes flat `template` used by some `/home/story/:id` payloads.
 */
export function pickHomeStoryPuzzleTemplate(ro) {
    if (!ro || typeof ro !== "object") return null;
    const t =
        ro.crossword?.crossTpl ??
        ro.crossword?.cross_tpl ??
        ro.crossTpl ??
        ro.grid?.gridTpl ??
        ro.grid?.grid_tpl ??
        ro.gridTpl ??
        (ro.template && typeof ro.template === "object" ? ro.template : null);
    return t && typeof t === "object" ? t : null;
}

/** True when {@link resolveHomeStoryPlayPath} can yield a `/story/play-*` URL (sync ids or fetchable template id). */
export function homeStoryHasPlayResolutionCandidate(ro) {
    if (!ro || typeof ro !== "object") return false;
    if (getHomeStoryPlayPath(ro)) return true;
    const type = homeStoryPuzzleType(ro);
    if (type !== "CROSSWORD" && type !== "WORD_SEARCH" && type !== "HIDDEN_MESSAGE") return false;
    return Boolean(getHomeStoryTemplateIdForPlayFetch(ro, type));
}

/** English title/text + image URL from flattened `story` on HomeStoryRo. */
export function getEnFromHomeStoryRo(ro) {
    const story = resolveHomeStoryBlob(ro);
    if (!story || typeof story !== "object") {
        return { title: "", text: "", imageUrl: "" };
    }
    const en = story.en ?? story.stories?.en ?? {};
    const title = typeof en?.title === "string" ? en.title : "";
    const text = typeof en?.text === "string" ? en.text : "";
    const imageUrl = typeof story.imageUrl === "string" ? story.imageUrl : "";
    return { title, text, imageUrl };
}

/**
 * Title/text/image for browse UI using content language (`english` | `french`, e.g. from `getContentLanguage()`).
 * French with empty fr falls back to en.
 */
export function getLocalizedStoryFromHomeStoryRo(ro, contentLanguage) {
    const story = resolveHomeStoryBlob(ro);
    if (!story || typeof story !== "object") {
        return { title: "", text: "", imageUrl: "" };
    }
    const imageUrl = typeof story.imageUrl === "string" ? story.imageUrl : "";
    const useFr = String(contentLanguage || "").toLowerCase() === "french";
    let block = useFr
        ? story.fr ?? story.stories?.fr ?? null
        : story.en ?? story.stories?.en ?? null;
    let title = typeof block?.title === "string" ? block.title : "";
    let text = typeof block?.text === "string" ? block.text : "";
    if (useFr && !String(title).trim() && !String(text).trim()) {
        const en = story.en ?? story.stories?.en ?? {};
        title = typeof en.title === "string" ? en.title : "";
        text = typeof en.text === "string" ? en.text : "";
    }
    if (!useFr && !String(title).trim() && !String(text).trim()) {
        const fr = story.fr ?? story.stories?.fr ?? {};
        title = typeof fr.title === "string" ? fr.title : "";
        text = typeof fr.text === "string" ? fr.text : "";
    }
    return { title, text, imageUrl };
}

/** Cover image for storybook / browse thumbnails (prefers icon, then preview, then full). */
export function pickStoryCoverImageUrl(ro) {
    const story = resolveHomeStoryBlob(ro);
    if (!story || typeof story !== "object") return "";
    const url =
        (typeof story.imageIcon400Url === "string" && story.imageIcon400Url.trim()) ||
        (typeof story.imagePreviewUrl === "string" && story.imagePreviewUrl.trim()) ||
        (typeof story.imageUrl === "string" && story.imageUrl.trim()) ||
        "";
    return url;
}

/**
 * Published sliding/jigsaw play targets for a home story row (`story.sliding` / `story.jigsaw`).
 * Play routes use the story UUID (`/puzzle/play-sliding/:storyId`, `/puzzle/play-jigsaw/:storyId`).
 */
/** Template language label source (`english` | `french`, …) from a home story row. */
export function getHomeStoryTemplateLanguage(ro) {
    const tpl = pickHomeStoryPuzzleTemplate(ro);
    const raw = tpl?.language ?? ro?.language ?? ro?.template?.language ?? "";
    return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Crossword / word-search / hidden-message link metadata for storybook (no image).
 * Returns null when the row has no playable word puzzle.
 */
export function getHomeStoryWordPuzzleLinkMeta(ro) {
    const type = getHomeStoryPuzzleType(ro);
    if (type !== "CROSSWORD" && type !== "WORD_SEARCH" && type !== "HIDDEN_MESSAGE") {
        return null;
    }
    const tpl = pickHomeStoryPuzzleTemplate(ro);
    if (isPuzzleSidecarDisabled(tpl)) return null;
    if (!homeStoryHasPlayResolutionCandidate(ro)) return null;
    const title =
        (typeof ro?.templateTitle === "string" && ro.templateTitle.trim()) ||
        (typeof tpl?.title === "string" && tpl.title.trim()) ||
        "";
    return {
        type,
        title: title || "—",
        language: getHomeStoryTemplateLanguage(ro),
        tags: normalizeStoryRoTags(ro?.tags ?? tpl?.tags),
        disabled: isPuzzleSidecarDisabled(tpl),
    };
}

export function getHomeStorySlidingJigsawLinks(ro) {
    const story = resolveHomeStoryBlob(ro);
    const storyId = getHomeStoryRowId(ro) || nonEmptyTrimmed(story?.id) || "";
    if (!storyId) {
        return { storyId: "", slidingPlayId: null, jigsawPlayId: null, slidingDisabled: false, jigsawDisabled: false };
    }
    const sliding = story?.sliding;
    const jigsaw = story?.jigsaw;
    const slidingPlayId = isPuzzleSidecarListedOnStorybook(sliding) ? storyId : null;
    const jigsawPlayId = isPuzzleSidecarListedOnStorybook(jigsaw) ? storyId : null;
    return {
        storyId,
        slidingPlayId,
        jigsawPlayId,
        slidingDisabled: isPuzzleSidecarDisabled(sliding),
        jigsawDisabled: isPuzzleSidecarDisabled(jigsaw),
    };
}

/** Id for `GET /home/story/{storyId}` from a list row. */
export function getHomeStoryRowId(ro) {
    if (!ro || typeof ro !== "object") return "";
    return nonEmptyTrimmed(ro.id) ?? resolveHomeStoryShareId(ro) ?? "";
}

/**
 * Instant play route when `/home/story/*` carries nested `crossword` / `grid` instance ids.
 * Otherwise {@link resolveHomeStoryPlayPath} uses GET `/home/puzzle/:templateId` to resolve instance ids (no `/v1` calls).
 */
export function getHomeStoryPlayPath(ro) {
    const type = homeStoryPuzzleType(ro);
    if (type === "CROSSWORD") {
        const crossPlayId = getHomeStoryCrossPlayInstanceId(ro);
        if (!crossPlayId) return null;
        return `/story/play-cross/${encodeURIComponent(crossPlayId)}`;
    }
    if (type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") {
        const gridPlayId = getHomeStoryGridPlayInstanceId(ro);
        if (!gridPlayId) return null;
        const enc = encodeURIComponent(gridPlayId);
        if (type === "WORD_SEARCH") return `/story/play-grid/word_search/${enc}`;
        return `/story/play-grid/hidden_message/${enc}`;
    }
    return null;
}

/** `type:templateId` → in-flight home puzzle fetch for play instance id */
const templateStoryPlayPathPromises = new Map();

function fetchStoryPlayPathByTemplate(ro) {
    const type = homeStoryPuzzleType(ro);
    const tplId = getHomeStoryTemplateIdForPlayFetch(ro, type);
    if (!tplId) return Promise.resolve(null);
    const cacheKey = `${type}:${tplId}`;
    const existing = templateStoryPlayPathPromises.get(cacheKey);
    if (existing) return existing;

    const promise = (async () => {
        try {
            const homePu = await ApiService.getData(`/home/puzzle/${encodeURIComponent(tplId)}`);
            if (type === "CROSSWORD") {
                const cid = extractCrosswordPlayInstanceIdFromPayload(homePu);
                return cid ? `/story/play-cross/${encodeURIComponent(cid)}` : null;
            }
            if (type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") {
                const gid = extractGridPlayInstanceIdFromPayload(homePu);
                if (!gid) return null;
                const enc = encodeURIComponent(gid);
                if (type === "HIDDEN_MESSAGE") return `/story/play-grid/hidden_message/${enc}`;
                return `/story/play-grid/word_search/${enc}`;
            }
        } catch {
            /* no path */
        }
        return null;
    })();

    templateStoryPlayPathPromises.set(cacheKey, promise);
    return promise;
}

/** Resolves playable `/story/play-*` path (sync ids on RO, else GET `/home/puzzle/:templateId`). */
export async function resolveHomeStoryPlayPath(ro) {
    try {
        const synced = getHomeStoryPlayPath(ro);
        if (synced) return synced;
        return await fetchStoryPlayPathByTemplate(ro);
    } catch {
        return null;
    }
}

/** EASY | MEDIUM | HARD from HomeStoryRo root or nested cross/grid tpl (matches puzzle cards). */
export function getHomeStoryDifficultyLevel(ro) {
    if (!ro || typeof ro !== "object") return null;
    const pick = (v) => {
        if (typeof v !== "string") return null;
        const s = v.trim();
        return s !== "" ? s.toUpperCase() : null;
    };
    const direct = pick(ro.difficultyLevel);
    if (direct) return direct;
    const ct = ro.crossword?.crossTpl ?? ro.crossTpl;
    const gt = ro.grid?.gridTpl ?? ro.gridTpl;
    const fromCt = pick(ct?.difficultyLevel);
    if (fromCt) return fromCt;
    const fromGt = pick(gt?.difficultyLevel);
    if (fromGt) return fromGt;
    return null;
}

export function truncateText(text, maxLen) {
    if (text == null || typeof text !== "string") return "";
    const t = text.trim();
    if (!t || t.length <= maxLen) return t;
    return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** `/home/story` row publish time (nested template for grids/cross, or story blob). */
export function getHomeStoryRowPublishedAtMs(row) {
    if (!row || typeof row !== "object") return null;
    const story = resolveHomeStoryBlob(row);
    const v =
        row.crossword?.crossTpl?.publishedAt ??
        row.crossword?.crossTpl?.published_at ??
        row.crossTpl?.publishedAt ??
        row.crossTpl?.published_at ??
        row.grid?.gridTpl?.publishedAt ??
        row.grid?.gridTpl?.published_at ??
        row.gridTpl?.publishedAt ??
        row.gridTpl?.published_at ??
        row.template?.publishedAt ??
        row.template?.published_at ??
        story?.publishedAt ??
        story?.published_at ??
        row.publishedAt ??
        row.published_at;
    if (v == null || v === "") return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/** Formatted publish date for storybook / browse UI (e.g. `May 18, 2026`). */
export function formatHomeStoryPublishedLabel(row) {
    const tpl = pickHomeStoryPuzzleTemplate(row);
    let raw = tpl?.publishedAt ?? tpl?.published_at;
    if (raw == null || raw === "") {
        const ms = getHomeStoryRowPublishedAtMs(row);
        if (ms != null) raw = ms;
    }
    if (raw == null || raw === "") return "";
    const d = UtilService.formatMDY(raw) ?? UtilService.formatDate(raw);
    return typeof d === "string" && d.trim() ? d.trim() : "";
}

export function isHomeStoryRowPublishedWithinLastDays(row, days) {
    const ms = getHomeStoryRowPublishedAtMs(row);
    if (ms == null) return false;
    const publishedDate = new Date(ms);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return publishedDate > cutoff;
}

/**
 * Home story row UUID for `/share/{id}/story` (same id as `/read-story/:storyId`).
 * Checks common shapes from GET `/home/cross/:id`, `/home/grid/:id`, and nested template blobs.
 */
export function resolveStoryShareIdFromPlayPayload(puzzleData) {
    if (!puzzleData || typeof puzzleData !== "object") return "";
    const t = puzzleData.template && typeof puzzleData.template === "object" ? puzzleData.template : null;
    const candidates = [
        puzzleData.storyId,
        puzzleData.homeStoryId,
        t?.storyId,
        t?.homeStoryId,
        puzzleData.story?.id,
        puzzleData.story?.storyId,
        t?.story?.id,
        t?.story?.storyId,
    ];
    for (const c of candidates) {
        const s = typeof c === "string" ? c.trim() : String(c ?? "").trim();
        if (s) return s;
    }
    return "";
}

/**
 * Build a HomeStoryRo-shaped object from a grid puzzle (e.g. GET /home/grid/:id) so
 * {@link getLocalizedStoryFromHomeStoryRo} and story browse cards can render it.
 */
export function buildStoryBrowseCardItemFromGridPuzzle(puzzle) {
    if (!puzzle || typeof puzzle !== "object") return null;
    const template =
        puzzle.template && typeof puzzle.template === "object" ? puzzle.template : {};
    const tid = template.id ?? puzzle.templateId ?? null;
    const puzzleType = String(puzzle.type ?? template.type ?? "HIDDEN_MESSAGE").toUpperCase();
    const item = {
        id: puzzle.storyId ?? puzzle.homeStoryId ?? null,
        templateId: tid,
        templateTitle: typeof puzzle.title === "string" ? puzzle.title : "—",
        puzzleType,
        tags: normalizeStoryRoTags(template.tags ?? puzzle.tags),
        grid: {
            id: puzzle.id,
            gridTpl: Object.keys(template).length ? template : undefined,
        },
        gridTpl: Object.keys(template).length ? template : undefined,
        template: Object.keys(template).length ? template : undefined,
    };
    if (puzzle.story && typeof puzzle.story === "object") {
        item.story = puzzle.story;
    }
    return item;
}

function puzzleSidecarState(sidecar) {
    if (!sidecar || typeof sidecar !== "object") return "";
    return String(sidecar.state || "").toUpperCase();
}

function isPuzzleSidecarDisabled(sidecar) {
    if (!sidecar || typeof sidecar !== "object") return false;
    const state = puzzleSidecarState(sidecar);
    return state === "DISABLED" || state === "DISCARDED" || sidecar.disabled === true;
}

function isPuzzleSidecarListedOnStorybook(sidecar) {
    return puzzleSidecarState(sidecar) === "PUBLISHED";
}

/** True when the story's word puzzle, sliding, or jigsaw is withdrawn from public play. */
export function isHomeStoryContentDisabled(ro) {
    const tpl = pickHomeStoryPuzzleTemplate(ro);
    const tplState = puzzleSidecarState(tpl);
    if (tplState && tplState !== "PUBLISHED") return true;
    if (isPuzzleSidecarDisabled(tpl)) return true;
    const story = resolveHomeStoryBlob(ro);
    return isPuzzleSidecarDisabled(story?.sliding) || isPuzzleSidecarDisabled(story?.jigsaw);
}

/** Shelf row: published story with a real cover image (no empty placeholder books). */
export function isHomeStorybookShelfRow(ro) {
    if (!ro || typeof ro !== "object") return false;
    if (isHomeStoryContentDisabled(ro)) return false;
    if (!resolveHomeStoryBlob(ro)) return false;
    if (!pickStoryCoverImageUrl(ro)) return false;
    const { title } = getLocalizedStoryFromHomeStoryRo(ro);
    return Boolean(String(title || ro?.templateTitle || "").trim());
}
